import { Link } from 'react-router-dom';
import { BedDouble, Bath, Car, Maximize, MapPin, ImageOff } from 'lucide-react';
import { brl } from './portalApi';
import type { PropertyCardData } from './portalApi';

export default function PropertyCard({ p }: { p: PropertyCardData }) {
  const isRent = p.rentPrice != null && p.rentPrice > 0;
  const price = isRent ? p.rentPrice : p.salePrice;
  const area = p.usableArea || p.builtArea || p.totalArea;

  return (
    <Link
      to={`/imovel/${p.id}`}
      className="group bg-white rounded-xl overflow-hidden border border-gray-200 shadow-sm hover:shadow-lg transition-shadow flex flex-col"
    >
      <div className="relative aspect-[4/3] bg-gray-100 overflow-hidden">
        {p.cover ? (
          <img
            src={p.cover}
            alt={p.title}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-300">
            <ImageOff className="w-10 h-10" />
          </div>
        )}
        <span className="absolute top-3 left-3 text-xs font-semibold px-2.5 py-1 rounded-full bg-white/90 text-gray-700 shadow">
          {p.typeLabel}
        </span>
        <span
          className={`absolute top-3 right-3 text-xs font-semibold px-2.5 py-1 rounded-full text-white shadow ${
            isRent ? 'bg-emerald-600' : 'bg-primary-600'
          }`}
        >
          {isRent ? 'Locação' : 'Venda'}
        </span>
      </div>

      <div className="p-4 flex flex-col flex-1">
        <p className="text-lg font-bold text-primary-700">
          {brl(price)}
          {isRent && <span className="text-xs font-normal text-gray-500">/mês</span>}
        </p>
        <h3 className="mt-1 font-medium text-gray-800 line-clamp-1">{p.title}</h3>
        <p className="mt-0.5 text-sm text-gray-500 flex items-center gap-1">
          <MapPin className="w-3.5 h-3.5 shrink-0" />
          <span className="line-clamp-1">
            {[p.neighborhood, p.city].filter(Boolean).join(', ') || 'Localização sob consulta'}
          </span>
        </p>

        <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-4 text-sm text-gray-600">
          {p.bedrooms != null && (
            <span className="flex items-center gap-1" title="Quartos">
              <BedDouble className="w-4 h-4 text-gray-400" /> {p.bedrooms}
            </span>
          )}
          {p.bathrooms != null && (
            <span className="flex items-center gap-1" title="Banheiros">
              <Bath className="w-4 h-4 text-gray-400" /> {p.bathrooms}
            </span>
          )}
          {p.parkingSpaces != null && (
            <span className="flex items-center gap-1" title="Vagas">
              <Car className="w-4 h-4 text-gray-400" /> {p.parkingSpaces}
            </span>
          )}
          {area != null && (
            <span className="flex items-center gap-1" title="Área">
              <Maximize className="w-4 h-4 text-gray-400" /> {area} m²
            </span>
          )}
        </div>

        <span className="mt-4 text-center text-sm font-semibold text-primary-600 group-hover:text-primary-700">
          Ver ficha completa →
        </span>
      </div>
    </Link>
  );
}
